import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Mail, Send, User, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { submitContactForm } from "@/services/contact/contactService";

const formSchema = z.object({
  name: z.string().min(2, { message: "Il nome deve contenere almeno 2 caratteri" }),
  email: z.string().email({ message: "Inserisci un indirizzo email valido" }),
  phone: z.string().optional(),
  message: z.string().min(10, { message: "Il messaggio deve contenere almeno 10 caratteri" }),
});

type ContactFormValues = z.infer<typeof formSchema>;

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactFormModal({ isOpen, onClose }: ContactFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isRecaptchaLoaded, executeRecaptcha, recaptchaError } = useRecaptcha();

  useEffect(() => {
    if (recaptchaError) {
      console.error("reCAPTCHA error:", recaptchaError);
      toast.error("Errore di sicurezza", {
        description: "Impossibile caricare il sistema di sicurezza. Ricarica la pagina.",
        position: "bottom-right"
      });
    }
  }, [recaptchaError]);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    
    try {
      if (!isRecaptchaLoaded) {
        throw new Error("Sistema di sicurezza non disponibile. Ricarica la pagina.");
      }
      
      const recaptchaToken = await executeRecaptcha('contact');
      
      // Ottieni informazioni aggiuntive per la sicurezza
      let ipAddress = null;
      let userAgent = navigator.userAgent;
      
      try {
        // Tenta di ottenere l'indirizzo IP (potrebbe fallire a causa di CORS)
        const ipResponse = await fetch('https://api.ipify.org?format=json')
          .then(res => res.json())
          .catch(() => null);
        
        if (ipResponse && ipResponse.ip) {
          ipAddress = ipResponse.ip;
        }
      } catch (error) {
        console.warn("Impossibile ottenere l'indirizzo IP:", error);
      }
      
      // Usa il servizio di contatto diretto invece dell'endpoint API
      const result = await submitContactForm({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        message: data.message,
        recaptchaToken,
        ipAddress,
        userAgent
      });
      
      if (!result.success) {
        throw new Error(result.message || "Si è verificato un errore durante l'invio del messaggio");
      }
      
      toast.success(result.message || "Messaggio inviato con successo! Ti contatteremo presto.");
      
      form.reset();
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Invio messaggio fallito", {
        description: error instanceof Error ? error.message : "Si è verificato un errore durante l'invio del messaggio",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled = isSubmitting || !isRecaptchaLoaded;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Contattaci</DialogTitle>
          <DialogDescription>
            Compila il modulo sottostante per richiedere informazioni. Ti risponderemo al più presto.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome e Cognome</FormLabel>
                  <FormControl>
                    <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                      <div className="px-3 py-2 border-r">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" placeholder="Mario Rossi" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                      <div className="px-3 py-2 border-r">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" placeholder="esempio@email.com" type="email" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefono (opzionale)</FormLabel>
                  <FormControl>
                    <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                      <div className="px-3 py-2 border-r">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" placeholder="+39 123 456 7890" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Inserisci un numero di telefono per essere ricontattato
                  </FormDescription>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Messaggio</FormLabel>
                  <FormControl>
                    <div className="flex flex-col border rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                      <div className="px-3 py-2 border-b flex items-center">
                        <MessageSquare className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-sm text-muted-foreground">La tua richiesta</span>
                      </div>
                      <Textarea 
                        className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" 
                        placeholder="Scrivi il tuo messaggio qui..." 
                        {...field} 
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="text-xs text-muted-foreground text-center mb-4">
              Questo sito è protetto da reCAPTCHA e si applicano la <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> e i <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Termini di Servizio</a> di Google.
            </div>
            
            <DialogFooter className="mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={submitDisabled}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? "Invio in corso..." : "Invia messaggio"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
